import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  Paper,
  Grid,
  CircularProgress,
  Alert
} from '@mui/material';
import { CloudUpload, Delete, Image as ImageIcon } from '@mui/icons-material';

const ImageUpload = ({ 
  value = [], 
  onChange, 
  multiple = true, 
  maxFiles = 10,
  accept = "image/*"
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;
    
    if (files.length > maxFiles) {
      setError(`Maximum ${maxFiles} files allowed`);
      return;
    }

    setError('');
    setUploading(true);

    try {
      const uploadPromises = files.map(file => uploadToCloudinary(file));
      const uploadedImages = await Promise.all(uploadPromises);
      
      if (multiple) {
        onChange([...value, ...uploadedImages]);
      } else {
        onChange(uploadedImages[0]);
      }
    } catch (err) {
      setError('Failed to upload images. Please try again.');
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const uploadToCloudinary = (file) => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_PRESET); // Unsigned upload preset
      formData.append('folder', process.env.REACT_APP_CLOUDINARY_FOLDER);

      const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
      if (!cloudName) {
        reject(new Error('Cloudinary cloud name not configured. Please set REACT_APP_CLOUDINARY_CLOUD_NAME in your .env file.'));
        return;
      }

      fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
        method: 'POST',
        body: formData,
      })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
          // Provide more helpful error messages
          let errorMessage = data.error.message;
          if (data.error.message === 'Upload preset not found') {
            errorMessage = `Upload preset ${process.env.REACT_APP_CLOUDINARY_PRESET} not found. Please create this preset in your Cloudinary dashboard. See setup instructions.`;
          } else if (data.error.message.includes('Invalid cloud name')) {
            errorMessage = 'Invalid Cloudinary cloud name. Please check your REACT_APP_CLOUDINARY_CLOUD_NAME environment variable.';
          }
          reject(new Error(errorMessage));
        } else {
          resolve(data.secure_url);
        }
      })
      .catch(error => {
        console.error('Upload error:', error);
        reject(new Error('Failed to upload image. Please check your internet connection and try again.'));
      });
    });
  };

  const removeImage = (index) => {
    if (multiple) {
      const newImages = value.filter((_, i) => i !== index);
      onChange(newImages);
    } else {
      onChange('');
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = Array.from(event.dataTransfer.files).filter(file => 
      file.type.startsWith('image/')
    );
    
    if (files.length > 0) {
      const syntheticEvent = {
        target: { files }
      };
      handleFileSelect(syntheticEvent);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const getDisplayValue = () => {
    if (multiple) {
      return Array.isArray(value) ? value : [];
    }
    return value ? [value] : [];
  };

  const displayImages = getDisplayValue();

  return (
    <Box>
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          textAlign: 'center',
          border: '2px dashed #ccc',
          borderRadius: 2,
          cursor: 'pointer',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
          mb: 2
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => fileInputRef.current?.click()}
      >
        <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
        <Typography variant="h6" gutterBottom>
          {multiple ? 'Upload Images' : 'Upload Image'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {multiple 
            ? `Drag and drop images here or click to select (max ${maxFiles})`
            : 'Drag and drop an image here or click to select'
          }
        </Typography>
        <Button
          variant="contained"
          component="span"
          startIcon={<ImageIcon />}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Choose Files'}
        </Button>
        
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          {error.includes('Upload preset') && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Quick Setup:</strong>
              </Typography>
              <Typography variant="body2" component="div">
                1. Go to your Cloudinary dashboard
              </Typography>
              <Typography variant="body2" component="div">
                2. Settings → Upload → Upload presets
              </Typography>
              <Typography variant="body2" component="div">
                3. Create preset named {process.env.REACT_APP_CLOUDINARY_PRESET} with "Unsigned" mode
              </Typography>
            </Box>
          )}
        </Alert>
      )}

      {uploading && (
        <Box display="flex" justifyContent="center" alignItems="center" p={2}>
          <CircularProgress size={24} sx={{ mr: 1 }} />
          <Typography variant="body2">Uploading images...</Typography>
        </Box>
      )}

      {displayImages.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {multiple ? 'Uploaded Images' : 'Uploaded Image'} ({displayImages.length})
          </Typography>
          <Grid container spacing={2}>
            {displayImages.map((imageUrl, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Paper
                  elevation={2}
                  sx={{
                    position: 'relative',
                    padding: 1,
                    borderRadius: 1,
                  }}
                >
                  <Box
                    component="img"
                    src={imageUrl}
                    alt={`Upload ${index + 1}`}
                    sx={{
                      width: '100%',
                      height: 150,
                      objectFit: 'cover',
                      borderRadius: 1,
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeImage(index)}
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      backgroundColor: 'rgba(255, 255, 255, 0.8)',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      },
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default ImageUpload;
